import { createAsyncThunk } from "@reduxjs/toolkit";
import axios, { AxiosError } from "axios";
import type { NavigateFunction } from "react-router-dom";
import type {
  CompareObject,
  ComparisonObjectValue,
} from "../../../services/api/instances/comparisonAPI/types";
import { createChatCompletions } from "../../../services/api/instances/completionsApi/methods";
import type { ChatCompletionsRequestObject } from "../../../services/api/instances/completionsApi/types";
import type { ModelsValue } from "../../../services/api/instances/modelsApi/types";
import {
  addChildToObject,
  createStorageObject,
  deleteChildObject,
  deleteStorageObject,
  getAllStorageObjects,
  updateStorageObject,
} from "../../../services/api/instances/storageApi/methods";
import i18n from "../../../services/i18n";
import { createStorageObjectApi } from "../../../services/lib/storageObjectManager";
import { getServicePagePath } from "../../../services/navigation/navigateToRoute";
import type { RootState } from "../../../services/redux/types";
import { getFilteredModels } from "../../../shared/utils/getFilteredModels";
import {
  getCountOfFolders,
  getCountOfObjects,
} from "../../../shared/utils/objectStorageFunctions";
import { getModelUrl } from "../../../shared/utils/objectStorageModels";
import { generateUniqueId, sortModelsByServerType } from "../helpers";
import {
  setComparisonObjects,
  setComparisonObjectValue,
  setSelectedComparisonObject,
} from "./slice";
import { StorageObject } from "../../../services/api/instances/storageApi/types/object_types";
import { getError } from "../../../services/lib/getError";

export const getComparisonObjectsAction = createAsyncThunk(
  "comparison/getAllComparisonObjects",
  async (_, { rejectWithValue }) => {
    try {
      return await getAllStorageObjects<ComparisonObjectValue>("llm_compare");
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const createComparisonObjectAction = createAsyncThunk(
  "comparison/createComparisonObject",
  async (
    {
      isFolder,
      name,
      withoutCount,
    }: { isFolder: "object" | "folder"; name?: string; withoutCount?: boolean },
    ThunkAPI,
  ) => {
    try {
      const { comparisonObjects, models } = (ThunkAPI.getState() as RootState)
        .comparisonState;
      const defaultName =
        isFolder === "folder"
          ? name
            ? name
            : i18n.t("llm.default_folder_name")
          : i18n.t("llm.default_name");
      const count =
        isFolder === "folder"
          ? getCountOfFolders(comparisonObjects)
          : getCountOfObjects(comparisonObjects);
      const comparisonObjectValue = [
        {
          id: generateUniqueId(),
          model: "",
          system_role_name: "",
          messages: [],
        },
      ];
      const comparisonObject = {
        friendly_name: !withoutCount
          ? `${defaultName} ${count + 1}`
          : `${defaultName}`,
        value:
          isFolder === "folder"
            ? "null"
            : JSON.stringify({
                llm_compare: comparisonObjectValue,
              }),
      };
      ThunkAPI.dispatch(setComparisonObjectValue(comparisonObjectValue));
      return await createStorageObject<ComparisonObjectValue>(
        comparisonObject,
        "llm_compare",
      );
    } catch (e: unknown) {
      const message = getError(e);
      return ThunkAPI.rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const updateComparisonObjectAction = createAsyncThunk(
  "comparison/updateComparisonObject",
  async (
    postObj: StorageObject<ComparisonObjectValue>,
    { rejectWithValue },
  ) => {
    try {
      return await updateStorageObject<ComparisonObjectValue>(
        postObj,
        "llm_compare",
      );
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const updateComparisonObjectsWithChildrenAction = createAsyncThunk(
  "comparison/updateComparisonObjectWithChildren",
  async (
    body: { childId: string | null; parentId: string },
    { rejectWithValue },
  ) => {
    try {
      const { childId, parentId } = body;

      return await addChildToObject(childId as string, parentId, "llm_compare");
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const deleteChildrenAction = createAsyncThunk(
  "comparison/deleteComparisonObjectChildren",
  async (body: { childId: string; parentId: string }, { rejectWithValue }) => {
    try {
      const { childId, parentId } = body;
      return await deleteChildObject(childId, parentId);
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const deleteComparisonObjectAction = createAsyncThunk(
  "comparison/deleteComparisonObject",
  async (body: { id: string; navigate: NavigateFunction }, thunkAPI) => {
    try {
      const { navigate, id } = body;
      const { comparisonObjects, selectedComparisonObject } = (
        thunkAPI.getState() as RootState
      ).comparisonState;
      const storage =
        createStorageObjectApi<ComparisonObjectValue>(comparisonObjects);
      const folder = storage.findParent(id);

      if (folder !== null) {
        const nextFolderComparison = folder.children?.filter(
          (comparison: StorageObject<ComparisonObjectValue>) =>
            comparison.id !== id,
        ) as StorageObject<ComparisonObjectValue>[];
        const updatedFolder = {
          ...folder,
          children: [
            ...(nextFolderComparison as StorageObject<ComparisonObjectValue>[]),
          ],
        };
        const lastFolderComparisonObject = nextFolderComparison?.at(-1);
        const updatedComparisonObjects = comparisonObjects.map((comparison) => {
          if (comparison.id === updatedFolder.id) {
            return updatedFolder;
          } else {
            return comparison;
          }
        });
        thunkAPI.dispatch(setComparisonObjects(updatedComparisonObjects));

        if (!nextFolderComparison[0]) {
          navigate(getServicePagePath(folder.id!, "llm_compare"));
          thunkAPI.dispatch(setSelectedComparisonObject(folder));
        } else if (selectedComparisonObject?.id === id) {
          thunkAPI.dispatch(
            setSelectedComparisonObject(
              lastFolderComparisonObject as StorageObject<ComparisonObjectValue>,
            ),
          );
          navigate(
            getServicePagePath(lastFolderComparisonObject?.id!, "llm_compare"),
          );
        }
      }

      if (folder === null) {
        const nextComparison = storage.removeById(id);
        const lastComparison = nextComparison[nextComparison.length - 1];

        thunkAPI.dispatch(setComparisonObjects(nextComparison));

        if (!nextComparison[0]) {
          navigate(getServicePagePath(null, "llm_compare"));
          thunkAPI.dispatch(setSelectedComparisonObject(null));
          thunkAPI.dispatch(setComparisonObjects([]));
        } else if (selectedComparisonObject?.id === id) {
          thunkAPI.dispatch(setSelectedComparisonObject(lastComparison));
          navigate(getServicePagePath(lastComparison.id!, "llm_compare"));
        }
      }

      return await deleteStorageObject(id, "llm_compare");
    } catch (e: unknown) {
      const message = getError(e);
      return thunkAPI.rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const getModelsObjectAction = createAsyncThunk(
  "comparison/getModels",
  async (_, { rejectWithValue }) => {
    try {
      const response =
        await getAllStorageObjects<ModelsValue>("ai_agents_models");
      const currentModels = response.find(
        (object) => object.friendly_name === "models_v2",
      );
      if (!currentModels) return;
      const models = getFilteredModels(
        currentModels.value.models,
        "llm_compare",
      );
      const sortModels = sortModelsByServerType(models);
      const servers = currentModels.value.servers;
      return { models: sortModels, servers };
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const createChatComparison = createAsyncThunk(
  "comparison/createChatComparison",
  async (objectValue: CompareObject[], ThunkAPI) => {
    const { models } = (ThunkAPI.getState() as RootState).comparisonState;
    const promises: Promise<any>[] = [];
    const abortController = new AbortController();

    try {
      objectValue.forEach((compareChat) => {
        const messages = compareChat.messages;
        const chatUrl = getModelUrl(models as ModelsValue, compareChat);
        const modelName = compareChat.model;
        const requestObject = {
          model: modelName,
          messages: messages,
        };
        promises.push(
          createChatCompletions(
            requestObject as ChatCompletionsRequestObject,
            chatUrl,
            { signal: abortController.signal },
          )
            .then((value) => ({
              status: "fulfilled",
              value,
              id: compareChat.id,
            }))
            .catch((reason) => ({
              status: "rejected",
              reason: reason.message,
              id: compareChat.id,
            })),
        );
      });

      const result = await Promise.race(promises);

      if (result.status === "fulfilled") {
        return result;
      } else if (result.status === "rejected") {
        return result;
      }
    } catch (e) {
      if (
        ThunkAPI.signal.aborted ||
        axios.isCancel(e) ||
        (e instanceof Error && e.name === "AbortError")
      ) {
        return ThunkAPI.rejectWithValue("Aborted");
      }
      const message = getError(e);
      return ThunkAPI.rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);
